
import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Loader2 } from 'lucide-react';
import { ServiceFormData } from './ServiceFormState';
import { TherapyTagSelector } from './TherapyTagSelector';
import { useTranslation } from 'react-i18next';
import { sessionFormats, currencies } from '@/utils/profile-options';

const HOURS_OPTIONS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const MINUTES_OPTIONS = [0, 15, 30, 45];

interface NewServiceFormProps {
  newService: ServiceFormData;
  selectedFormats: Record<string, boolean>;
  isSubmitting: boolean;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onSelectChange: (name: string, value: any) => void;
  onFormatChange: (format: string, checked: boolean) => void;
  onToggleActive: (value: boolean) => void;
  onToggleFreeSession: (value: boolean) => void;
  onToggleParentalConsent: (value: boolean) => void;
  onToggleAdultsOnly: (value: boolean) => void;
  onTherapyTagsChange: (tags: string[]) => void;
  onCustomTagsChange: (tags: string[]) => void;
  onSubmit: (e: React.FormEvent) => void;
}

export function NewServiceForm({
  newService,
  selectedFormats,
  isSubmitting,
  onInputChange,
  onSelectChange,
  onFormatChange,
  onToggleActive,
  onToggleFreeSession,
  onToggleParentalConsent,
  onToggleAdultsOnly,
  onTherapyTagsChange,
  onCustomTagsChange,
  onSubmit,
}: NewServiceFormProps) {
  const { t } = useTranslation('service-form');

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      {/* Service Title + Duration */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="service_name">Service Name<span className="text-red-500">*</span></Label>
          <Input
            id="service_name"
            name="service_name"
            value={newService.service_name}
            onChange={onInputChange}
            placeholder="Enter service name"
            required
          />
        </div>

        <div className="space-y-2">
          <Label>{t('durationHours')} / {t('durationMinutes')}</Label>
          <div className="grid grid-cols-2 gap-2">
            <Select
              value={newService.duration_hours.toString()}
              onValueChange={(value) => onSelectChange('duration_hours', Number(value))}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('durationHours')} />
              </SelectTrigger>
              <SelectContent>
                {HOURS_OPTIONS.map((h) => (
                  <SelectItem key={h} value={h.toString()}>
                    {h}h
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={newService.duration_minutes_part.toString()}
              onValueChange={(value) => onSelectChange('duration_minutes_part', Number(value))}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('durationMinutes')} />
              </SelectTrigger>
              <SelectContent>
                {MINUTES_OPTIONS.map((m) => (
                  <SelectItem key={m} value={m.toString()}>
                    {m}m
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Therapy Tags Section */}
      <div className="space-y-2">
        <Label>
          {t('therapyTags')} <span className="text-red-500">*</span>
        </Label>
        <p className="text-sm text-muted-foreground">
          {t('therapyTagsDescription')}
        </p>
        <TherapyTagSelector
          value={newService.therapy_tags}
          onChange={onTherapyTagsChange}
          customTags={newService.custom_therapy_tags}
          onCustomTagsChange={onCustomTagsChange}
        />
      </div>

      {/* Price + Session Format */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="price">Price<span className="text-red-500">*</span></Label>
          <div className="flex">
            <Select 
              value={newService.currency} 
              onValueChange={(value) => onSelectChange('currency', value)}
            >
              <SelectTrigger className="w-[100px] rounded-r-none border-r-0">
                <SelectValue placeholder="Currency" />
              </SelectTrigger>
              <SelectContent>
                {currencies.map((currency) => (
                  <SelectItem key={currency.value} value={currency.value}>
                    {currency.value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              id="price"
              name="price"
              type="number"
              min="0"
              step="0.01"
              value={newService.price}
              onChange={onInputChange}
              placeholder="0.00"
              className="rounded-l-none"
              required={!newService.is_free_session}
              disabled={newService.is_free_session}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Session Format<span className="text-red-500">*</span></Label>
          <div className="flex flex-wrap gap-4 pt-2">
            {sessionFormats.map((format) => (
              <div key={format.value} className="flex items-center space-x-2">
                <Checkbox 
                  id={`format-${format.value}`}
                  checked={selectedFormats[format.value] || false}
                  onCheckedChange={(checked) => 
                    onFormatChange(format.value, checked as boolean)
                  }
                />
                <label 
                  htmlFor={`format-${format.value}`}
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {format.label}
                </label>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          name="description"
          value={newService.description}
          onChange={onInputChange}
          placeholder="Describe what this service includes..."
          className="min-h-[100px]"
        />
      </div>

      {/* Session Instructions */}
      <div className="space-y-2">
        <Label htmlFor="session_instructions">Session Instructions (Private)</Label>
        <Textarea
          id="session_instructions"
          name="session_instructions"
          value={newService.session_instructions || ''}
          onChange={onInputChange}
          placeholder="Provide details like Zoom link or in-person address. Visible only after booking."
          className="min-h-[100px]"
        />
        <p className="text-sm text-muted-foreground">Only visible to clients after booking and sent in confirmation email.</p>
      </div>

      {/* Free Session Toggle */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="is_free_session">Free / Intern Session</Label>
          <Switch 
            id="is_free_session"
            checked={newService.is_free_session}
            onCheckedChange={onToggleFreeSession}
          />
        </div>
        <p className="text-sm text-muted-foreground">
          {newService.is_free_session ? 'This is marked as a free or intern session' : 'This is a regular paid service'}
        </p>
      </div>

      {/* Service Requirements */}
      <div className="space-y-4 border-t pt-4">
        <h3 className="text-lg font-medium">Service Requirements</h3>
        
        {/* Parental Consent */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="parental_consent_required">Parental Consent Required</Label>
            <Switch 
              id="parental_consent_required"
              checked={newService.parental_consent_required || false}
              onCheckedChange={onToggleParentalConsent}
            />
          </div>
          <p className="text-sm text-muted-foreground">
            {newService.parental_consent_required ? 'Parental consent will be required for minors' : 'No parental consent requirement'}
          </p>
        </div>

        {/* Adults Only */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="adults_only">Only Accepting Clients Aged 18 and Above</Label>
            <Switch 
              id="adults_only"
              checked={newService.adults_only || false}
              onCheckedChange={onToggleAdultsOnly}
            />
          </div>
          <p className="text-sm text-muted-foreground">
            {newService.adults_only ? 'Only clients 18+ can book this service' : 'All ages can book this service'}
          </p>
        </div>
      </div>

      {/* Active Status */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="is_active">Service Status</Label>
          <Switch 
            id="is_active"
            checked={newService.is_active}
            onCheckedChange={onToggleActive}
          />
        </div>
        <p className="text-sm text-muted-foreground">
          {newService.is_active ? 'This service will be available for booking' : 'This service will be hidden from clients'}
        </p>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end pt-4 border-t">
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Service'
          )}
        </Button>
      </div>
    </form>
  );
}
